import type { RouteRecordRaw } from 'vue-router'
import {
  handleAuthRedirect,
  authGuard,
  beforeEnvironment,
  beforeFeature,
  adminGuard
} from './guards'

// Every route lives under /app - the marketing pages at '/' are served by the
// Astro site in src/home, not by this app. The router base stays '/' (see
// quasar.config.ts > build.vueRouterBase), so these paths are the full URL
// paths the browser sees.
const routes: RouteRecordRaw[] = [
  {
    path: '/app',
    component: () => import('layouts/EnvironmentsLayout.vue'),
    meta: { auth: true },
    beforeEnter: [handleAuthRedirect, authGuard],
    children: [
      { path: '', name: 'environments', component: () => import('pages/EnvironmentsPage.vue') },
      {
        path: 'create',
        name: 'environment-create',
        component: () => import('pages/EnvironmentCreatePage.vue')
      }
    ]
  },
  {
    path: '/app/:environmentId',
    name: 'app',
    component: () => import('layouts/AppLayout.vue'),
    meta: { auth: true, environment: true },
    beforeEnter: [handleAuthRedirect, authGuard, beforeEnvironment],
    children: [
      {
        path: '',
        name: 'app-feature-home',
        beforeEnter: [beforeFeature],
        component: () => import('pages/MainFeatureContainer.vue')
      },
      {
        path: 'admin',
        name: 'admin',
        component: () => import('layouts/AdminLayout.vue'),
        beforeEnter: [adminGuard],
        children: [
          {
            path: '',
            name: 'admin-home',
            redirect: (to) => `/app/${to.params.environmentId}/admin/users`
          },
          {
            path: 'users',
            name: 'admin-users',
            component: () => import('src/components/admin/AdminUsers/AdminUsersPage.vue')
          },
          {
            path: 'catalog',
            name: 'admin-catalog',
            component: () => import('src/components/admin/AdminCatalog/AdminCatalogPage.vue')
          },
          {
            path: 'models',
            name: 'admin-models',
            component: () => import('src/components/admin/AdminModels/AdminModelsPage.vue')
          },
          {
            path: 'models/:modelId',
            name: 'admin-model-view',
            component: () => import('src/components/admin/AdminModels/AdminModelViewPage.vue'),
            props: true
          },
          {
            path: 'environment/general',
            name: 'admin-environment-general',
            component: () =>
              import('src/components/admin/AdminEnvironment/AdminEnvironmentGeneralPage.vue')
          },
          {
            path: 'environment/deployment',
            name: 'admin-environment-deployment',
            component: () =>
              import('src/components/admin/AdminEnvironment/AdminEnvironmentDeploymentPage.vue')
          },
          {
            path: 'environment/apikeys',
            name: 'admin-environment-apikeys',
            component: () =>
              import('src/components/admin/AdminEnvironment/AdminEnvironmentApiKeysPage.vue')
          },
          {
            path: 'environment/secrets',
            name: 'admin-environment-secrets',
            component: () =>
              import('src/components/admin/AdminEnvironment/AdminEnvironmentSecretsPage.vue')
          },
          {
            path: 'environment/certificates',
            name: 'admin-environment-certificates',
            component: () =>
              import('src/components/admin/AdminEnvironment/AdminEnvironmentCertificatesPage.vue')
          },
          {
            path: 'environment/jobs',
            name: 'admin-environment-jobs',
            component: () =>
              import('src/components/admin/AdminEnvironment/AdminEnvironmentJobsPage.vue')
          },
          {
            path: 'environment/configuration',
            name: 'admin-environment-configuration',
            component: () =>
              import('src/components/admin/AdminEnvironment/AdminEnvironmentConfigPage.vue')
          },
          {
            path: 'data-catalog',
            name: 'admin-data-catalog',
            component: () => import('src/components/admin/AdminData/AdminDataSourcesPage.vue')
          },
          {
            path: 'usage',
            name: 'admin-usage',
            component: () => import('src/components/admin/AdminUsage/AdminUsagePage.vue')
          },
          {
            path: 'features',
            name: 'admin-features',
            component: () => import('src/components/admin/AdminFeatures/AdminFeatures.vue')
          },
          {
            path: 'features/:featureId/queries',
            name: 'admin-feature-queries',
            component: () => import('src/components/admin/AdminFeatures/AdminFeatureQueries.vue'),
            props: true
          },
          {
            path: 'devices',
            name: 'admin-devices',
            component: () => import('src/components/admin/AdminDevices/AdminDevicesPage.vue')
          },
          {
            path: 'devices/:deviceId',
            name: 'admin-device',
            component: () => import('src/components/admin/AdminDevices/AdminDeviceInfo.vue'),
            props: true
          },
          {
            path: 'import-mappings',
            name: 'admin-import-mappings',
            component: () =>
              import('src/components/admin/AdminImportMappings/AdminImportMappings.vue')
          },
          {
            path: 'flows',
            name: 'admin-flows',
            component: () => import('src/components/flows/FlowsPage.vue')
          },
          {
            path: 'flows/:flowId',
            name: 'admin-flow-edit',
            component: () => import('src/components/flows/FlowEditPage.vue'),
            props: true
          }
        ]
      },
      {
        // Self-service profile: contact details and notification preferences
        path: 'profile',
        name: 'app-profile',
        component: () => import('pages/UserProfilePage.vue')
      },
      // Isolated dashboard tile rendering: /app/:environmentId/:featureId/:tileId
      // This must come before the generic feature route to avoid being captured there.
      // Use query params showNavigation=false&showPageHeader=false when rendering headless.
      {
        path: ':featureId/edit',
        name: 'app-feature-edit',
        beforeEnter: [beforeFeature],
        component: () => import('pages/FeatureEditPage.vue'),
        props: true
      },
      {
        path: ':featureId/:tileId',
        name: 'app-feature-tile',
        beforeEnter: [beforeFeature],
        component: () => import('pages/RenderTilePage.vue'),
        props: true
      },
      {
        // when /app/:environmentId/:featureId is matched
        path: ':featureId',
        name: 'app-feature',
        beforeEnter: [beforeFeature],
        component: () => import('pages/MainFeatureContainer.vue')
      }
    ]
  },

  // Used for popup redirect (doesn't implement handleAuthRedirect).
  // Must stay under /app so the gateway routes it to this app and not to the
  // marketing site; src/services/auth.ts points MSAL's popup redirectUri here.
  {
    path: '/app/blank',
    component: () => import('pages/BlankPage.vue')
  },
  // DEV-only Playwright harness for the FeatureHeader (AB#4426). The
  // webServer runs `pnpm dev`, so this route exists under test and never ships.
  // The harness lives next to the specs it exists for, not in src/pages.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/app/playwright/chat-error-model',
          name: 'playwright-chat-error-model',
          component: () => import('test/playwright/ChatErrorModelHarness.vue')
        },
        {
          path: '/app/playwright/chat-tool-approval',
          name: 'playwright-chat-tool-approval',
          component: () => import('test/playwright/ChatToolApprovalHarness.vue')
        },
        {
          path: '/app/playwright/chat-approval-protocol',
          name: 'playwright-chat-approval-protocol',
          component: () => import('test/playwright/ChatApprovalProtocolHarness.vue')
        },
        {
          path: '/app/playwright/feature-header',
          name: 'playwright-feature-header',
          component: () => import('test/playwright/FeatureHeaderPlaywrightHarness.vue')
        },
        {
          // Drawer feature headers (AB#4434): Info/Relations/Files/Chat have no
          // main page, so their headers can only be reached by mounting the
          // drawer components. Driven by test/playwright/drawer-headers.spec.ts.
          path: '/app/playwright/drawer-headers',
          name: 'playwright-drawer-headers',
          component: () => import('test/playwright/DrawerFeatureHeaderHarness.vue')
        },
        {
          path: '/app/playwright/grid-dashboard-size-to-content',
          name: 'playwright-grid-dashboard-size-to-content',
          // eslint-disable-next-line no-secrets/no-secrets -- DEV-only harness import path.
          component: () => import('test/playwright/GridDashboardSizeToContentHarness.vue')
        },
        {
          path: '/app/playwright/grid-dashboard-full-height',
          name: 'playwright-grid-dashboard-full-height',
          // eslint-disable-next-line no-secrets/no-secrets -- DEV-only harness import path.
          component: () => import('test/playwright/GridDashboardFullHeightHarness.vue')
        },
        {
          path: '/app/playwright/grid-dashboard-shared-viewport',
          name: 'playwright-grid-dashboard-shared-viewport',
          // eslint-disable-next-line no-secrets/no-secrets -- DEV-only harness import path.
          component: () => import('test/playwright/GridDashboardSharedViewportHarness.vue')
        },
        {
          path: '/app/playwright/grid-dashboard-shared-width',
          name: 'playwright-grid-dashboard-shared-width',
          // eslint-disable-next-line no-secrets/no-secrets -- DEV-only harness import path.
          component: () => import('test/playwright/GridDashboardSharedWidthHarness.vue')
        },
        {
          path: '/app/playwright/grid-dashboard-wrapper',
          name: 'playwright-grid-dashboard-wrapper',
          // eslint-disable-next-line no-secrets/no-secrets -- DEV-only harness import path.
          component: () => import('test/playwright/GridDashboardWrapperHarness.vue')
        },
        {
          // The duration parameter's `dateOnly` option: whether a 'YYYY-MM-DD' mask makes
          // q-date emit a date-only string, and whether that string resolves to the right
          // local day. Driven by test/playwright/time-range-parameter.spec.ts.
          path: '/app/playwright/time-range-parameter',
          name: 'playwright-time-range-parameter',
          component: () => import('test/playwright/TimeRangeParameterHarness.vue')
        },
        {
          // The twin edit dialog's property layout: there is no feature page that can be
          // entered without an environment and the dialog is opened from a context menu,
          // so the harness mounts it directly. Driven by
          // test/playwright/twin-edit-dialog-layout.spec.ts.
          path: '/app/playwright/twin-edit-dialog',
          name: 'playwright-twin-edit-dialog',
          component: () => import('test/playwright/TwinEditDialogHarness.vue')
        }
      ]
    : []),
  // Test-only harness for the Forge model alignment panel page (AB#4425), driven by
  // the Playwright spec in test/playwright. DEV-only so it never ships.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/app/test-forge-model-alignment',
          // eslint-disable-next-line no-secrets/no-secrets -- a component path, not a credential
          component: () => import('test/playwright/ForgeModelAlignmentHarness.vue')
        },
        {
          path: '/app/test-catalog-tree-search',
          // eslint-disable-next-line no-secrets/no-secrets -- a component path, not a credential
          component: () => import('test/playwright/CatalogTreeSearchHarness.vue')
        },
        {
          path: '/app/test-catalog-autodesk',
          // eslint-disable-next-line no-secrets/no-secrets -- a component path, not a credential
          component: () => import('test/playwright/CatalogAutodeskHarness.vue')
        }
      ]
    : []),
  // Test-only harness for the ForgeViewerTile scrollbar defect, driven by
  // test/playwright/forge-tile-scroll.spec.ts. DEV-only so it never ships.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/app/test-forge-tile-scroll',
          component: () => import('test/playwright/ForgeTileScrollHarness.vue')
        }
      ]
    : []),
  // Test-only harness for the GeoTIFF layer style panel page (AB#4152), driven by
  // test/playwright/geotiff-styling.spec.ts. DEV-only so it never ships.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/app/test-geotiff-styling',
          // eslint-disable-next-line no-secrets/no-secrets -- a component path, not a credential
          component: () => import('test/playwright/GeoTIFFStylingHarness.vue')
        }
      ]
    : []),
  // Test-only harness for the catalog entry point (AB#4152 P4d), driven by
  // test/playwright/catalog-entry.spec.ts. DEV-only so it never ships.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/app/test-catalog-entry',
          component: () => import('test/playwright/CatalogEntryHarness.vue')
        }
      ]
    : []),
  ...(import.meta.env.DEV
    ? [
        {
          path: '/app/test-catalog-browser',
          component: () => import('test/playwright/CatalogBrowserHarness.vue')
        }
      ]
    : []),
  ...(import.meta.env.DEV
    ? [
        {
          path: '/app/test-catalog-link',
          component: () => import('test/playwright/CatalogLinkHarness.vue')
        }
      ]
    : []),
  // Always leave this as last one
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/Error404Page.vue')
  }
]
export default routes
